// English, the source language (docs/plans/phase3-portal-feedback-kiswahili.md §8; ADR 0035).
// Every key here must exist in sw.ts too (TypeScript checks it). `{name}` marks a value
// filled in at run time; the Kiswahili text must use the same placeholders (a test checks).
// Drug names, test names, codes, identifiers, units and facility names are data and never
// pass through these files. Dates, times and money keep the house formats in every language.

export const en = {
  // --- Languages ----------------------------------------------------------------------------
  'language.label': 'Language',
  'language.en': 'English',
  'language.sw': 'Kiswahili',
  'language.saved': 'Language saved',
  'language.description':
    'The language of the menus, page titles and common buttons. Most screens are still in English.',
  'language.machineNotice':
    'The Kiswahili words have not yet been checked by a native speaker. Report anything that reads wrong.',

  // --- Staff shell ---------------------------------------------------------------------------
  'shell.skipToContent': 'Skip to content',
  'shell.menu': 'Menu',
  'shell.closeMenu': 'Close menu',
  'shell.expandSidebar': 'Expand sidebar',
  'shell.collapseSidebar': 'Collapse sidebar',
  'shell.mainNavigation': 'Main',
  'shell.facility': 'Facility: {name}',
  'shell.account': 'Account: {name}',
  'shell.accountAndAppearance': 'Account and appearance',
  'shell.signOut': 'Sign out',
  'shell.facilityChanged': 'Facility changed',
  'shell.nowWorkingAt': 'Now working at {name}',
  'shell.offline':
    'Offline. Drafts are kept on this device. Saving to Wodi waits for the connection.',
  'shell.offlineWaiting':
    'Offline. Drafts are kept on this device, and {count} saved on this device will send when connected.',
  'shell.sending': 'Sending {count} saved on this device.',
  'shell.action': '1 action',
  'shell.actions': '{n} actions',

  'nav.group.frontDesk': 'Front desk',
  'nav.group.clinical': 'Clinical',
  'nav.group.pharmacyStores': 'Pharmacy & stores',
  'nav.group.billingClaims': 'Billing & claims',
  'nav.group.reports': 'Reports',
  'nav.group.administration': 'Administration',
  'nav.home': 'Home',
  'nav.reception': 'Reception',
  'nav.recordMerges': 'Record merges',
  'nav.appointments': 'Appointments',
  'nav.records': 'Records',
  'nav.charts': 'Charts',
  'nav.referrals': 'Referrals',
  'nav.deathsAndBirths': 'Deaths and births',
  'nav.feedback': 'Feedback',
  'nav.casualty': 'Casualty',
  'nav.queue': 'Queue',
  'nav.lab': 'Lab',
  'nav.imaging': 'Imaging',
  'nav.wards': 'Wards',
  'nav.theatre': 'Theatre',
  'nav.maternity': 'Maternity',
  'nav.mortuary': 'Mortuary',
  'nav.pharmacy': 'Pharmacy',
  'nav.stores': 'Stores',
  'nav.procurement': 'Procurement',
  'nav.cashier': 'Cashier',
  'nav.approvals': 'Approvals',
  'nav.claims': 'Claims',
  'nav.insurance': 'Insurance',
  'nav.reports': 'Reports',
  'nav.staff': 'Staff',
  'nav.settings': 'Settings',

  // Page titles of the pages the navigation opens (titles.ts maps the English title here).
  'page.home': 'Home',
  'page.welcome': 'Welcome, {name}',
  'page.account': 'Account',
  'page.accountDescription': 'Settings that are yours alone.',
  'page.patientPortal': 'Patient portal',
  'page.feedback': 'Feedback',

  // --- Common actions and states -----------------------------------------------------------
  'common.tryAgain': 'Try again',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.save': 'Save',
  'common.back': 'Back',
  'common.loading': 'Loading',
  'common.reloadPage': 'Reload the page',
  'common.goHome': 'Go to the home page',

  // --- Errors --------------------------------------------------------------------------------
  'error.title': 'Something went wrong',
  'error.screenStopped':
    'This screen stopped working because of a problem in Wodi. Reload the page to continue. If it keeps happening, report a problem to your administrator.',
  'error.notFoundTitle': 'Page not found',
  'error.notFoundBody':
    'There is no page at this address. Check the link, or start again from the home page.',
  'error.generic': 'Something went wrong. Try again.',
  'errors.offline': "Can't reach Wodi. Check the internet connection, then try again.",
  'errors.not_signed_in': 'Your session has ended. Sign in again to continue.',
  'errors.forbidden': "You don't have permission to do this. Ask an administrator if you need it.",
  'errors.rate_limited': 'Too many attempts. Wait a minute, then try again.',
  'errors.service_unavailable': 'This service is not available right now. Try again later.',
  'errors.internal_error':
    'Something went wrong in Wodi. Try again; if it keeps happening, report it.',
  'errors.not_found': 'That could not be found. It may have been removed, or the link is wrong.',
  'errors.invalid_credentials':
    'That is not right, or the account is locked after too many attempts. Check and try again.',

  // --- Staff sign-in ------------------------------------------------------------------------
  'signIn.title': 'Sign in',
  'signIn.email': 'Email',
  'signIn.password': 'Password',
  'signIn.submit': 'Sign in',
  'signIn.pending': 'Signing in…',
  'signIn.emailInvalid': 'Enter your email address, like name@example.test.',
  'signIn.passwordMissing': 'Enter your password.',
  'signIn.forgot': 'Forgot your password? Ask an administrator at your facility to reset it.',
  'signIn.reason.expired': 'Your session ended. Sign in again to continue.',
  'signIn.reason.expired_unsent':
    "Your session ended. Actions saved on this device that had not been sent to Wodi were deleted. After signing in, check the patient's record and enter them again.",
  'signIn.reason.signed_out': 'You are signed out.',

  // --- Patient portal ------------------------------------------------------------------------
  'portal.title': 'Patient portal',
  'portal.loadingFacility': 'Loading the patient portal',
  'portal.unknownAddress':
    "This patient portal address isn't right, or the portal is switched off. Check the address with the facility.",
  'portal.signIn.heading': 'Sign in to see your appointments, bills and results',
  'portal.signIn.phone': 'Your mobile number',
  'portal.signIn.phoneHelp': 'The number the facility has for you. We send a code to it by SMS.',
  'portal.signIn.phoneInvalid': 'Enter a Kenyan mobile number, for example 0712 345 678.',
  'portal.signIn.sendCode': 'Send code',
  'portal.signIn.sending': 'Sending…',
  'portal.signIn.codeSent':
    'If this number is registered for the portal, a code is on its way by SMS. It works for 5 minutes.',
  'portal.signIn.code': 'Code from the SMS',
  'portal.signIn.codeInvalid': 'Enter the 6 digits from the SMS.',
  'portal.signIn.submit': 'Sign in',
  'portal.signIn.pending': 'Signing in…',
  'portal.signIn.resend': 'Send a new code',
  'portal.signIn.resendIn': 'You can ask for a new code in {seconds} seconds.',
  'portal.signIn.otherNumber': 'Use another number',
  'portal.signIn.notRegistered':
    'No code? Ask at the facility to be registered for the patient portal. You need to agree to it there.',
  'portal.signedOut': 'You are signed out.',
  'portal.sessionEnded': 'You were signed out after 15 minutes without use. Sign in again.',
  'portal.signOut': 'Sign out',
  'portal.whose': 'Whose records',
  'portal.yourself': 'You',
  'portal.child': 'Child',
  'portal.patientNumber': 'Patient number',
  'portal.offline':
    "You're offline. What you see may be out of date. Reconnect to load the latest.",

  'portal.appointments.title': 'Upcoming appointments',
  'portal.appointments.empty': 'No upcoming appointments.',
  'portal.appointments.with': 'With {name}',
  'portal.appointments.kind.new': 'New visit',
  'portal.appointments.kind.follow_up': 'Follow-up',
  'portal.appointments.cancel': 'Cancel appointment',
  'portal.appointments.cancelTitle': 'Cancel this appointment?',
  'portal.appointments.cancelBody':
    'The facility will see that you cancelled it. To book again, call or visit the facility.',
  'portal.appointments.cancelReason': 'Reason (optional)',
  'portal.appointments.keep': 'Keep it',
  'portal.appointments.cancelled': 'Appointment cancelled',

  'portal.bills.title': 'Bills and payments',
  'portal.bills.empty': 'No bills to show.',
  'portal.bills.totalOwed': 'Still to pay: {amount}',
  'portal.bills.nothingOwed': 'Nothing to pay on the bills shown.',
  'portal.bills.onlyShown':
    'This shows bills you can see here. For a full statement, ask at the cash desk.',
  'portal.bills.visit': 'Visit {number}',
  'portal.bills.payer': 'Paid by: {name}',
  'portal.bills.item': 'Item',
  'portal.bills.youPay': 'You pay',
  'portal.bills.covered': 'Covered',
  'portal.bills.cancelledLine': 'Cancelled',
  'portal.bills.due': 'Your share',
  'portal.bills.paid': 'Paid',
  'portal.bills.owed': 'Still to pay',
  'portal.bills.unpriced': 'Some items are not priced yet, so the amount may change.',
  'portal.bills.receipts': 'Payments',
  'portal.bills.receipt': 'Receipt {number}',
  'portal.bills.howToPay':
    'Pay with M-Pesa: Lipa na M-Pesa, Pay Bill, business number {paybill}, account number {account}.',
  'portal.bills.payAtFacility': 'Pay at the facility cash desk.',
  'portal.bills.notProcessor': 'You pay the facility directly. Wodi never takes your money.',

  'portal.results.title': 'Lab results',
  'portal.results.empty': 'No lab results to show.',
  'portal.results.whatIsShown':
    'Only results the laboratory has checked and released are shown. Some results are discussed with you at the facility instead. Ask your clinician what your results mean.',
  'portal.results.test': 'Test',
  'portal.results.result': 'Result',
  'portal.results.range': 'Usual range',
  'portal.results.withheld': 'Ask at the facility',
  'portal.results.released': 'Released {date}',
  // A final culture: outcome and organisms only (lab QC and microbiology plan Q12).
  'portal.results.culture': 'Culture',
  'portal.results.culture.no_growth': 'No growth',
  'portal.results.culture.growth': 'Grown: {organisms}',
  'portal.results.culture.pending': 'Not finished',
  'portal.results.culture.sensitivities': 'Ask your clinician about the antibiotics tested.',
  'portal.results.flag.normal': '',
  'portal.results.flag.low': 'Low',
  'portal.results.flag.high': 'High',
  'portal.results.flag.abnormal': 'Abnormal',
  'portal.results.flag.no_range': '',
  'portal.results.flag.not_applicable': '',
  'portal.results.flag.critical_low': '',
  'portal.results.flag.critical_high': '',
  'portal.results.flag.critical': '',

  'portal.allergies.title': 'Allergies',
  'portal.allergies.none': 'No known allergies',
  'portal.allergies.notRecorded': 'Allergies not recorded',
  'portal.allergies.notRecordedHelp': 'Tell the facility about any allergies at your next visit.',
  'portal.allergies.reaction': 'Reaction: {reaction}',
  'portal.allergies.high': 'High risk',
  'portal.allergies.unconfirmed': 'Not yet confirmed',

  'portal.feedback.title': 'Your feedback',
  'portal.feedback.waiting': 'How was your visit on {date} at {facility}?',
  'portal.feedback.noneWaiting': 'No visits waiting for your feedback.',
  'portal.feedback.rate': 'Rate this visit',
  'portal.feedback.given': 'You rated your visit on {date}: {rating}',
  'portal.feedback.reply': 'Reply from the facility: {reply}',

  'feedback.rating': 'Your rating',
  'feedback.rating.1': 'Very poor',
  'feedback.rating.2': 'Poor',
  'feedback.rating.3': 'Fair',
  'feedback.rating.4': 'Good',
  'feedback.rating.5': 'Very good',
  'feedback.ratingMissing': 'Choose a rating.',
  'feedback.comment': 'Anything you want to tell us (optional)',
  'feedback.commentHelp': 'Please do not write about your health here. Up to 1000 characters.',
  'feedback.send': 'Send feedback',
  'feedback.sending': 'Sending…',
  'feedback.thanks': 'Thank you. Your feedback was sent to the facility.',
  'feedback.link.title': 'Rate your visit',
  'feedback.link.about': 'Your visit to {facility} on {date}',
  'feedback.link.loading': 'Checking your link',
  'feedback.link.gone':
    'This link has expired or was already used. You can give feedback at the facility.',
} as const;

export type MessageKey = keyof typeof en;
